import { useState, useEffect, useRef } from "react";
import { Box, Typography, Alert, Grid, FormControlLabel } from "@mui/material";
import { useMutation } from "@apollo/client";
import BaseButton from "../../BaseButton";
import BaseInput from "../../BaseInput";
import BaseCheckbox from "../../BaseCheckbox";
import { toast } from "react-toastify";
import { CreatePendingPatient } from "../../../apollo-client/api-query";
import { formatDateOfBirth } from "../../../utils/format-date";

// Helper to validate proband kit code format (14 digits)
const isValidProbandKitCode = (code) => {
  if (!code) return true; // Empty is valid (optional field)
  return /^\d{14}$/.test(code);
};

// Helper to format price strings with thousand separators (e.g., "$1234.00" -> "$1,234.00")
const formatPriceWithCommas = (priceStr) => {
  if (!priceStr) return priceStr;
  // Match the dollar sign, integer part, and decimal part
  const match = priceStr.match(/^(\$?)(\d+)(\.\d+)?$/);
  if (!match) return priceStr;
  const [, dollar, intPart, decimal] = match;
  const formattedInt = parseInt(intPart, 10).toLocaleString('en-US');
  return `${dollar}${formattedInt}${decimal || ''}`;
};

export default function CheckoutStep({
  data,
  onNext,
  onBack,
  onClose,
  kitPriceCents,
  kitPriceDollars,
  familyDiscountEnabled,
  familyDiscountPriceCents,
  familyDiscountPriceDollars
}) {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    email: data.email || '',
    emailConfirm: data.emailConfirm || '',
    applyFamilyDiscount: data.applyFamilyDiscount || false,
    probandKitCode: data.probandKitCode || '',
    probandKitCodeConfirm: data.probandKitCodeConfirm || '',
  });
  const firstInputRef = useRef(null);

  // Auto-focus first input on mount
  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);
  const [formErrors, setFormErrors] = useState({});

  // Determine which price to display based on family discount selection
  const displayPrice = formatPriceWithCommas(
    formData.applyFamilyDiscount && familyDiscountEnabled
      ? familyDiscountPriceDollars
      : kitPriceDollars
  );

  const [createPendingPatient] = useMutation(CreatePendingPatient, {
    onCompleted: (result) => {
      console.log('Pending patient created:', result);
      const { success, inviteUrl, emailSentTo } = result.createPendingPatient;

      if (success) {
        // Pass the result to the confirmation step
        onNext({
          ...formData,
          inviteUrl,
          emailSentTo,
        });
      } else {
        setError('Failed to create patient invitation. Please try again.');
        setProcessing(false);
      }
    },
    onError: (error) => {
      console.error('Pending patient creation error:', error);

      // Map backend errors to user-friendly messages
      const errorMessage = error.message || 'Failed to send invitation. Please try again.';
      const userFriendlyMessages = {
        'Physician pricing not found': 'The Add Patient feature is not configured for your account. Please contact support.',
        'Physician pricing is inactive': 'The Add Patient feature is currently disabled for your account. Please contact support.',
        'Kit code not found': 'The kit code was not recognized. Please go back and verify the barcode.',
        'Kit is not available for assignment': 'This kit has already been assigned to another patient. Please use a different kit.',
        'Kit code already in use': 'This kit code is already associated with a pending patient. Please use a different kit.',
        'Pending patient exists for this email': 'An invitation has already been sent to this email address.',
        // Family discount errors
        'Family discount pricing is not enabled for this physician': 'Family discount is not available for your account.',
        'Proband kit code must be exactly 14 digits': 'The proband kit code must be exactly 14 digits.',
        'Proband kit code cannot be the same as the patient\'s kit code': 'The proband kit code cannot be the same as the patient\'s kit code.',
        'Proband kit code does not exist': 'The proband kit code was not found in our system.',
      };

      const displayMessage = userFriendlyMessages[errorMessage] || errorMessage;
      setError(displayMessage);
      toast.error('Failed to send invitation');
      setProcessing(false);
    }
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.emailConfirm.trim()) {
      newErrors.emailConfirm = 'Please confirm the email address';
    } else if (formData.email !== formData.emailConfirm) {
      newErrors.emailConfirm = 'Email addresses do not match';
    }

    // Validate proband kit code if family discount is applied and code is provided
    if (formData.applyFamilyDiscount && formData.probandKitCode) {
      if (!isValidProbandKitCode(formData.probandKitCode)) {
        newErrors.probandKitCode = 'Proband kit code must be exactly 14 digits';
      } else if (formData.probandKitCode !== formData.probandKitCodeConfirm) {
        newErrors.probandKitCodeConfirm = 'Kit codes do not match';
      } else if (formData.probandKitCode === data.kitCode) {
        newErrors.probandKitCode = 'Proband kit code cannot be the same as the patient\'s kit code';
      }
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle "Send Invitation" button click
  const handleSendInvitation = async () => {
    // Validate form first
    if (!validateForm()) {
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // Prepare input matching backend PatientCheckoutInput spec
      const input = {
        // Patient registration fields
        firstName: data.patientFirstName,
        lastName: data.patientLastName,
        dateOfBirth: data.patientDateOfBirth,
        email: formData.email,
        middleName: "",
        phoneNumber: data.patientPhone, // Required - collected in PhysicianOrderStep
        sex: data.patientSex,
        kitCode: data.kitCode,

        // Physician metadata
        physicianNotes: data.physicianNotes || '',

        // Legal/compliance confirmations
        noBoneMarrowTransplant: data.noBoneMarrowTransplant,
        noRecentBloodTransfusion: data.noRecentBloodTransfusion,
        // Note: membershipAgreementAccepted and informedConsentAccepted are now
        // collected separately in the patient portal after they click the invite link

        // Family discount fields
        isFamilyDiscount: formData.applyFamilyDiscount,
        probandKitCode: formData.applyFamilyDiscount && formData.probandKitCode ? formData.probandKitCode : null,
      };

      console.log('Creating pending patient with input:', input);

      // Create pending patient - this will send an invite email
      await createPendingPatient({ variables: { input } });

      // Success handling happens in the mutation's onCompleted callback
    } catch (err) {
      console.error('Invitation error:', err);
      setError(err.message || 'Failed to send invitation. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <Box>
      {/* Title */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h1"
          sx={{
            color: '#5A3BC7',
            fontSize: { xs: '24px', sm: '28px' },
            fontWeight: 600,
            mb: 1
          }}
        >
          Review & Send Invitation
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#5B87EF',
            fontSize: '14px'
          }}
        >
          Review the patient details and send them an invitation to complete registration
        </Typography>
      </Box>

      {/* Order Summary */}
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: '8px',
          border: '1px solid #ECEFF1',
          background: '#F6F9FC'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: '#46545F',
            fontSize: '16px',
            fontWeight: 600,
            mb: 2
          }}
        >
          Patient Summary
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', mr: 1 }}>
                Name:
              </Typography>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600 }}>
                {data.patientFirstName} {data.patientLastName}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', mr: 1 }}>
                Date of Birth:
              </Typography>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600 }}>
                {formatDateOfBirth(data.patientDateOfBirth)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', mr: 1 }}>
                Phone:
              </Typography>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600 }}>
                {data.patientPhone}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', mr: 1 }}>
                Kit Code:
              </Typography>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600, fontFamily: 'monospace' }}>
                {data.kitCode}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: '1px solid #ECEFF1',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6" sx={{ color: '#46545F', fontSize: '16px', fontWeight: 600 }}>
            Patient Price:
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h6" sx={{ color: '#5A3BC7', fontSize: '20px', fontWeight: 700 }}>
              {displayPrice}
            </Typography>
            {formData.applyFamilyDiscount && familyDiscountEnabled && (
              <Typography variant="caption" sx={{ color: '#4CAF50', fontSize: '12px' }}>
                Family Discount Applied
              </Typography>
            )}
          </Box>
        </Box>

        {/* Family Discount Option - Only show if enabled for this physician */}
        {familyDiscountEnabled && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #ECEFF1' }}>
            <FormControlLabel
              control={
                <BaseCheckbox
                  checked={formData.applyFamilyDiscount}
                  onChange={(e) => handleChange('applyFamilyDiscount', e.target.checked)}
                  disabled={processing}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px' }}>
                  Apply Family Discount ({formatPriceWithCommas(familyDiscountPriceDollars)})
                </Typography>
              }
            />

            {/* Proband Kit Code - Only show when family discount is checked */}
            {formData.applyFamilyDiscount && (
              <Box sx={{ mt: 2, pl: 4 }}>
                <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 500, mb: 1 }}>
                  Proband Kit Code <span style={{ color: '#888' }}>(optional)</span>
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                  Enter the kit code of the original family member (proband) if available
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <BaseInput
                      fullWidth
                      placeholder="14-digit kit code"
                      value={formData.probandKitCode}
                      onChange={(e) => handleChange('probandKitCode', e.target.value.replace(/\D/g, '').slice(0, 14))}
                      error={!!formErrors.probandKitCode}
                      helperText={formErrors.probandKitCode}
                      disabled={processing}
                      inputProps={{ maxLength: 14 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          boxShadow: '0px 2px 10px 0px #5B87EF33',
                          fontFamily: 'monospace'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <BaseInput
                      fullWidth
                      placeholder="Confirm kit code"
                      value={formData.probandKitCodeConfirm}
                      onChange={(e) => handleChange('probandKitCodeConfirm', e.target.value.replace(/\D/g, '').slice(0, 14))}
                      error={!!formErrors.probandKitCodeConfirm}
                      helperText={formErrors.probandKitCodeConfirm}
                      disabled={processing || !formData.probandKitCode}
                      inputProps={{ maxLength: 14 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          boxShadow: '0px 2px 10px 0px #5B87EF33',
                          fontFamily: 'monospace'
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Patient Email */}
      <Typography
        variant="h6"
        sx={{
          color: '#46545F',
          fontSize: '16px',
          fontWeight: 600,
          mb: 1
        }}
      >
        Patient Email Address
      </Typography>

      <Alert
        severity="info"
        sx={{
          mb: 2,
          '& .MuiAlert-message': {
            fontSize: '13px'
          }
        }}
      >
        The patient will use this email to receive their invitation and create their account. Please ensure it is correct.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 500, mb: 1 }}>
            Email <span style={{ color: '#E73E3E' }}>*</span>
          </Typography>
          <BaseInput
            ref={firstInputRef}
            fullWidth
            type="email"
            placeholder="patient@example.com"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            error={!!formErrors.email}
            helperText={formErrors.email}
            disabled={processing}
            sx={{
              '& .MuiOutlinedInput-root': {
                boxShadow: '0px 2px 10px 0px #5B87EF33'
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 500, mb: 1 }}>
            Confirm Email <span style={{ color: '#E73E3E' }}>*</span>
          </Typography>
          <BaseInput
            fullWidth
            type="email"
            placeholder="patient@example.com"
            value={formData.emailConfirm}
            onChange={(e) => handleChange('emailConfirm', e.target.value)}
            error={!!formErrors.emailConfirm}
            helperText={formErrors.emailConfirm}
            disabled={processing}
            sx={{
              '& .MuiOutlinedInput-root': {
                boxShadow: '0px 2px 10px 0px #5B87EF33'
              }
            }}
          />
        </Grid>
      </Grid>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}
      >
        <BaseButton
          type="button"
          onClick={onBack}
          disabled={processing}
          sx={{
            width: { xs: '100%', sm: '140px' },
            background: '#46545F',
            '&:hover': {
              background: '#2A3342'
            }
          }}
        >
          Back
        </BaseButton>

        <BaseButton
          type="button"
          onClick={handleSendInvitation}
          loading={processing}
          disabled={processing || !formData.email.trim() || !formData.emailConfirm.trim()}
          buttonType="primary"
          sx={{
            width: { xs: '100%', sm: '200px' }
          }}
        >
          {processing ? 'Sending...' : 'Send Invitation'}
        </BaseButton>
      </Box>
    </Box>
  );
}
